import { useNuxtApp, useState } from "#imports";
import { useCompare } from "~/composables/useCompare";

type CompareActionInput = {
  id: string | number;
  title?: string;
  article?: string;
  brand?: string;
  price?: number | string;
  discountPrice?: number | string;
  priceBadgeLabel?: string;
  image?: string;
  unit?: string;
  currentlyCompared?: boolean;
};

const normalizeId = (value: string | number | null | undefined) =>
  value === null || value === undefined
    ? ""
    : String(value).replace(/[\s\u00A0]+/g, "").trim();

export const useCompareAction = () => {
  const nuxtApp = useNuxtApp();
  const $useOneCApi = nuxtApp?.$useOneCApi as
    | ((action: string, payload?: Record<string, any>) => Promise<any>)
    | undefined;

  const compareUpdatingMap = useState<Record<string, boolean>>(
    "compare:updating-map",
    () => ({}),
  );

  const { addToCompare, removeFromCompare, isCompared } = useCompare();

  const isCompareUpdating = (id: string | number | null | undefined) => {
    const normalizedId = normalizeId(id);
    if (!normalizedId) return false;
    return Boolean(compareUpdatingMap.value[normalizedId]);
  };

  const setCompareUpdating = (
    id: string | number | null | undefined,
    value: boolean,
  ) => {
    const normalizedId = normalizeId(id);
    if (!normalizedId) return;
    compareUpdatingMap.value = {
      ...compareUpdatingMap.value,
      [normalizedId]: value,
    };
  };

  const addProductToCompare = async (input: CompareActionInput) => {
    const normalizedId = normalizeId(input.id);
    if (!normalizedId) return false;
    if (isCompared(normalizedId) || isCompareUpdating(normalizedId)) return false;

    if (typeof $useOneCApi !== "function") {
      console.error("API client is unavailable, cannot update compare list");
      return false;
    }

    setCompareUpdating(normalizedId, true);

    try {
      await $useOneCApi("addProductToCompareList", {
        product_id: normalizedId,
      });

      addToCompare({
        id: normalizedId,
        title: input.title,
        article: input.article,
        brand: input.brand,
        price: input.price,
        discountPrice: input.discountPrice,
        priceBadgeLabel: input.priceBadgeLabel,
        image: input.image,
        unit: input.unit,
      });

      return true;
    } catch (error) {
      console.error("Не удалось добавить товар в сравнение", error);
      return false;
    } finally {
      setCompareUpdating(normalizedId, false);
    }
  };

  const deleteProductFromCompare = async (
    id: string | number | null | undefined,
  ) => {
    const normalizedId = normalizeId(id);
    if (!normalizedId) return false;
    if (isCompareUpdating(normalizedId)) return false;

    if (typeof $useOneCApi !== "function") {
      console.error("API client is unavailable, cannot update compare list");
      return false;
    }

    setCompareUpdating(normalizedId, true);

    try {
      await $useOneCApi("deleteProductFromCompareList", {
        product_id: normalizedId,
      });

      removeFromCompare(normalizedId);
      return true;
    } catch (error) {
      console.error("Не удалось удалить товар из сравнения", error);
      return false;
    } finally {
      setCompareUpdating(normalizedId, false);
    }
  };

  const toggleProductInCompare = async (input: CompareActionInput) => {
    const normalizedId = normalizeId(input.id);
    if (!normalizedId || isCompareUpdating(normalizedId)) return false;

    const shouldDelete =
      typeof input.currentlyCompared === "boolean"
        ? input.currentlyCompared
        : isCompared(normalizedId);

    if (shouldDelete) {
      await deleteProductFromCompare(normalizedId);
      return false;
    }

    return addProductToCompare(input);
  };

  return {
    addProductToCompare,
    deleteProductFromCompare,
    toggleProductInCompare,
    isCompareUpdating,
    isCompared,
  };
};
